import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { observer } from 'mobx-react-lite'
import { StoreContext, rootStore } from '@/stores'
import { Toaster } from '@/components/ui/sonner'
import { useEffect } from 'react'
import { useAuthStore, useTenantStore } from '@/stores'
import { AuthStatus } from '@/stores/AuthStore'
import MainLayout from '@/components/MainLayout'
import Login from '@/containers/auth/Login'
import NotificationSend from '@/containers/NotificationSend'
import NotificationHistory from '@/containers/NotificationHistory'
import NotificationTagList from '@/containers/NotificationTagList'

const PrivateRoute = observer(({ children }: { children: React.ReactNode }) => {
  const authStore = useAuthStore()
  
  // 初期状態の場合はローディング表示
  if (authStore.authStatus === AuthStatus.INITIAL) {
    return <div className="flex min-h-screen items-center justify-center">
      <div className="text-muted-foreground">Loading...</div>
    </div>
  }
  
  if (!authStore.isAuthenticated) {
    return <Navigate to="/auth/login" replace />
  }
  
  return <MainLayout>{children}</MainLayout>
})

const AppContent = observer(() => {
  const authStore = useAuthStore()
  const tenantStore = useTenantStore()

  useEffect(() => {
    const initialize = async () => {
      const success = await authStore.checkAuth()
      
      // URLパラメーターからなりすまし先テナントIDを取得
      const urlParams = new URLSearchParams(window.location.search)
      const spoofingTenantId = urlParams.get('spoofingTenantId')
      
      if (success) {
        // なりすまし状態の引き継ぎ
        if (spoofingTenantId !== null && authStore.user) {
          if (spoofingTenantId === '') {
            // 空のIDが渡された場合、なりすまし管理を解除
            authStore.endSpoofing()
          } else {
            // 元のテナントIDを保存
            authStore.originalTenantId = authStore.user.tenant
            // なりすまし先のテナントIDを設定
            authStore.user = {
              ...authStore.user,
              tenant: spoofingTenantId
            }
            // localStorageにも保存（リロード対応）
            localStorage.spoofingTenantId = spoofingTenantId
          }
        }
        
        if (authStore.currentTenantId) {
          // ユーザーの所属テナント情報を取得してプランを設定
          await tenantStore.getTenant(authStore.currentTenantId)
        }
      }
    }
    initialize()
  }, []) // 空の依存配列で初回のみ実行

  return (
    <>
      <Routes>
        <Route path="/auth/login" element={<Login />} />
        <Route
          path="/"
          element={
            <PrivateRoute>
              <div className="space-y-6 mb-6">
                <h1 className="text-3xl font-bold">ダッシュボード</h1>
                <p className="text-muted-foreground">
                  hogeCast へようこそ
                </p>
              </div>
            </PrivateRoute>
          }
        />
        <Route
          path="/notifications/send"
          element={
            <PrivateRoute>
              <NotificationSend />
            </PrivateRoute>
          }
        />
        <Route
          path="/notifications/history"
          element={
            <PrivateRoute>
              <NotificationHistory />
            </PrivateRoute>
          }
        />
        <Route
          path="/notifications/tags"
          element={
            <PrivateRoute>
              <NotificationTagList />
            </PrivateRoute>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <Toaster />
    </>
  )
})

function App() {
  return (
    <StoreContext.Provider value={rootStore}>
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
    </StoreContext.Provider>
  )
}

export default App
